/**
 * Mobile navigation drawer (popover): behaviors that the Popover API and CSS cannot cover.
 *
 * Imported for its side effects (no exports): it wires up the drawer, or no-ops if none is present.
 */

/**
 * Minimum travel (px) along the drawer's axis for a swipe to count as a close gesture.
 */
const SWIPE_CLOSE_THRESHOLD = 80;

/**
 * Maximum cross-axis drift (px) tolerated before a gesture reads as a scroll, not a swipe.
 */
const SWIPE_CROSS_TOLERANCE = 60;

function initMobileDrawer(): void {
	const menu = document.getElementById('mobile-drawer');
	if (!menu) {
		return;
	}

	// 1. Close on navigation, or when an element opts in via `data-mobile-drawer-close`.
	menu.querySelectorAll<HTMLElement>('a[href], [data-mobile-drawer-close]').forEach((element) => {
		element.addEventListener('click', () => menu.hidePopover());
	});

	// 2. Swipe-to-close, in whichever direction the drawer slides off-screen (its `data-position`).
	// Track on `pointerdown`, decide on `pointerup`. Touch only: a mouse or trackpad drag can't dismiss it.
	let startX = 0;
	let startY = 0;

	menu.addEventListener(
		'pointerdown',
		(event: PointerEvent) => {
			if (event.pointerType !== 'touch') {
				return;
			}
			startX = event.clientX;
			startY = event.clientY;
		},
		{ passive: true },
	);

	menu.addEventListener(
		'pointerup',
		(event: PointerEvent) => {
			if (event.pointerType !== 'touch' || !menu.matches(':popover-open')) {
				return;
			}
			const deltaX = event.clientX - startX;
			const deltaY = event.clientY - startY;
			let closed: boolean;
			if (menu.dataset.position === 'right') {
				closed = deltaX > SWIPE_CLOSE_THRESHOLD && Math.abs(deltaY) < SWIPE_CROSS_TOLERANCE;
			} else if (menu.dataset.position === 'top') {
				closed = deltaY < -SWIPE_CLOSE_THRESHOLD && Math.abs(deltaX) < SWIPE_CROSS_TOLERANCE;
			} else if (menu.dataset.position === 'bottom') {
				closed = deltaY > SWIPE_CLOSE_THRESHOLD && Math.abs(deltaX) < SWIPE_CROSS_TOLERANCE;
			} else {
				closed = deltaX < -SWIPE_CLOSE_THRESHOLD && Math.abs(deltaY) < SWIPE_CROSS_TOLERANCE;
			}
			if (closed) {
				menu.hidePopover();
			}
		},
		{ passive: true },
	);

	// 3. Close once the toggle is hidden (desktop navigation has taken over).
	// Observe the document element, not the toggle: a `display:none` element stops emitting `ResizeObserver` callbacks.
	// Watching it directly misses the change; `checkVisibility()` then confirms the toggle is genuinely gone.
	const toggle = document.querySelector<HTMLElement>(`[popovertarget="${menu.id}"]`);
	if (toggle) {
		const observer = new ResizeObserver(() => {
			if (menu.matches(':popover-open') && !toggle.checkVisibility()) {
				menu.hidePopover();
			}
		});
		observer.observe(document.documentElement);
	}
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initMobileDrawer);
} else {
	initMobileDrawer();
}
